import { useCallback } from 'react';
import { useMutation } from 'redux-query-react';
import { prepareCartTrackingData } from '../utils/tracking.utils';
import { trackAddToCart } from '../utils/tracking.utils';
import { ProductSelectedInfo } from '../types/product.types';
import { IProductConfig } from '../types/product/detail.types';
import { IBuyBackProduct } from 'components/BuyBack/interfaces/buyBackInterfaces';
import formProductRequest, { IProductRequest } from '../data/requests/product/data/formProductRequest';
import { postRequest } from 'data/requests/postRequest';
import isLoggedIn from '../helpers/auth/isLoggedIn';
import addBuyBackProductsRequest from '../components/BuyBack/requests/addBuyBackProductsRequest';
import quoteIdRequest from '../data/requests/product/quoteIdRequest';
import crossSaleRequest from '../data/requests/product/crossSaleRequest';
import checkoutInbankRentalRequest from '../data/requests/product/inbankRental/checkoutInbankRentalRequest';
import crossSaleRequestBySku from '../data/requests/product/crossSaleRequestBySku';
import { CartType } from 'components/Cart/Enum/CartType';
import { URL_PARAMS } from '../constants/product.constants';
import { HttpStatusCode } from '../enums/HttpStatusCode';
import { empty } from '../helpers/empty';
import { extractErrors } from '../helpers/request/extractErrors';
import Toaster from 'vkid-ui/lib/Components/Toaster/index';

export interface UseCartOperationsProps {
    productConfig: IProductConfig;
    selectedProductInformation: ProductSelectedInfo;
    customer?: { id?: number };
    buyBackProducts: IBuyBackProduct[];
    cartFlowType: string;
    inbankRentalMetaResponse?: string;
    onSuccess: (message: string) => void;
    onError: (error: string | undefined) => void;
}

export interface UseCartOperationsReturn {
    addToCart: (cartFlowTypePassed?: string) => Promise<void>;
    addToCartInbankRental: () => Promise<void>;
    handleBuyBackProducts: () => void;
    addBuyBackProductsReq: (devices: IBuyBackProduct[]) => Promise<{ status: number; body: unknown }>;
}

export const useCartOperations = ({
    productConfig,
    selectedProductInformation,
    customer,
    buyBackProducts,
    cartFlowType,
    inbankRentalMetaResponse,
    onSuccess,
    onError,
}: UseCartOperationsProps): UseCartOperationsReturn => {
    const [, quoteRequest] = useMutation(() => quoteIdRequest());
    const [, checkCrossSaleRequest] = useMutation((quoteItemId: string) => crossSaleRequest(quoteItemId));
    const [, addToCartRequest] = useMutation((data: IProductRequest, quoteIdMask: string, cartFlowTypePassed: string) =>
        postRequest({
            method: 'POST',
            type: 'cartAddResponse',
            url: isLoggedIn() ? `carts/mine/items` : `guest-carts/${quoteIdMask}/items`,
            data: { ...data, cartFlowType: cartFlowTypePassed },
        }),
    );
    const [, addBuyBackProductsReq] = useMutation((devices: IBuyBackProduct[]) =>
        addBuyBackProductsRequest({ devices }),
    );
    const [, addInbankRentalRequest] = useMutation((data: IProductRequest) => checkoutInbankRentalRequest(data));
    const [, checkCrossSaleBySkuRequest] = useMutation((sku: string) => crossSaleRequestBySku(sku));

    const addBuyBackProductsReqCallback = useCallback(
        async (devices: IBuyBackProduct[]): Promise<{ status: number; body: unknown }> => {
            try {
                const result = await addBuyBackProductsReq(devices);
                return { status: result.status || HttpStatusCode.OK, body: result.body || result };
            } catch (error) {
                const err = error as Error;
                return { status: HttpStatusCode.BAD_REQUEST, body: { message: err.message } };
            }
        },
        [addBuyBackProductsReq],
    );

    const handleBuyBackProducts = useCallback(() => {
        void addBuyBackProductsReqCallback(buyBackProducts).then((buyBackRes) => {
            if (buyBackRes.status !== HttpStatusCode.OK) {
                const errorBody = buyBackRes.body as { message?: string };
                if (errorBody?.message) {
                    onError(errorBody.message);
                }
            }
        });
    }, [buyBackProducts, addBuyBackProductsReqCallback, onError]);

    const addToCart = useCallback(
        async (cartFlowTypePassed?: string): Promise<void> => {
            onError(undefined);

            try {
                const quoteIdResponse = await quoteRequest();
                if (quoteIdResponse.status !== 200) {
                    window.location.reload();
                    return;
                }
                const { quoteId } = quoteIdResponse.body as { quoteId: string };

                const productRequestData: IProductRequest = formProductRequest(
                    quoteId,
                    productConfig.sku.value,
                    productConfig.type,
                    selectedProductInformation.selectedAttributes,
                );

                const cartFlowTypeToUse = !empty(cartFlowTypePassed) ? cartFlowTypePassed : cartFlowType;

                // Use redux-query mutation for adding to cart
                const response = await addToCartRequest(productRequestData, quoteId, cartFlowTypeToUse);

                if (response.status !== 200) {
                    const errors = extractErrors(response);
                    if (errors?.error) {
                        onError(errors.error);
                    }
                } else {
                    // Handle buy-back products using redux-query
                    if (buyBackProducts.length > 0) {
                        void addBuyBackProductsReqCallback(buyBackProducts).then((buyBackRes) => {
                            if (buyBackRes.status !== HttpStatusCode.OK) {
                                const errorBody = buyBackRes.body as { message?: string };
                                if (errorBody?.message) {
                                    onError(errorBody.message);
                                }
                            }
                        });
                    }

                    onSuccess(productConfig.successFormat.replace('%1', productConfig.name));

                    const cartTrackingData = prepareCartTrackingData(
                        productConfig,
                        selectedProductInformation,
                        customer?.id?.toString(),
                    );
                    trackAddToCart(cartTrackingData);

                    const responseBody = response.body as { item_id?: string };
                    if (!responseBody?.item_id) {
                        return;
                    }

                    const checkCrossSaleResponse = await checkCrossSaleRequest(responseBody.item_id);

                    const crossSaleBody = checkCrossSaleResponse.body as { url?: string };
                    if (crossSaleBody?.url) {
                        if (cartFlowTypeToUse === CartType.SLICE3_CART) {
                            window.location.href = `${crossSaleBody.url}?${URL_PARAMS.SLICE3}`;
                        } else if (cartFlowTypeToUse === CartType.ESTO_SLICE3_CART) {
                            window.location.href = `${crossSaleBody.url}?${URL_PARAMS.ESTO_SLICE3}`;
                        } else {
                            window.location.href = crossSaleBody.url;
                        }
                    }
                }
            } catch (error) {
                const err = error as Error;
                const errorMessage = err.message || 'Failed to add product to cart';
                onError(errorMessage);
            }
        },
        [
            productConfig,
            selectedProductInformation,
            customer,
            buyBackProducts,
            cartFlowType,
            onSuccess,
            onError,
            addToCartRequest,
            quoteRequest,
            checkCrossSaleRequest,
            addBuyBackProductsReqCallback,
        ],
    );

    const addToCartInbankRental = useCallback(async (): Promise<void> => {
        try {
            const productRequestData: IProductRequest = formProductRequest(
                '',
                productConfig.sku.value,
                productConfig.type,
                selectedProductInformation.selectedAttributes,
                inbankRentalMetaResponse,
            );

            const response = await addInbankRentalRequest(productRequestData);

            if (response.status !== 200) {
                const responseBody = response.body as { message?: string };
                if (responseBody.message) {
                    Toaster.addToast({
                        intent: 'danger',
                        text: responseBody.message,
                        asHtml: true,
                    });
                }
            } else {
                // Handle buy-back products
                if (buyBackProducts.length > 0) {
                    handleBuyBackProducts();
                }

                const cartTrackingData = prepareCartTrackingData(
                    productConfig,
                    selectedProductInformation,
                    customer?.id?.toString(),
                );
                trackAddToCart(cartTrackingData);

                // Check for cross-sale redirect using SKU
                const checkCrossSaleResponse = await checkCrossSaleBySkuRequest(
                    selectedProductInformation.sku || productConfig.sku.value,
                );

                const crossSaleBody = checkCrossSaleResponse.body as { url?: string };
                if (crossSaleBody?.url) {
                    window.location.href = crossSaleBody.url;
                } else {
                    const responseBody = response.body as { message?: string };
                    if (responseBody.message) {
                        Toaster.addToast({
                            intent: 'success',
                            text: responseBody.message,
                            asHtml: true,
                        });
                    }
                }
            }
        } catch (error) {
            const err = error as Error;
            Toaster.addToast({
                intent: 'danger',
                text: err.message,
                asHtml: true,
            });
        }
    }, [
        productConfig,
        selectedProductInformation,
        customer,
        buyBackProducts,
        inbankRentalMetaResponse,
        onSuccess,
        onError,
        addInbankRentalRequest,
        checkCrossSaleBySkuRequest,
        addBuyBackProductsReqCallback,
    ]);

    return {
        addToCart,
        addToCartInbankRental,
        handleBuyBackProducts,
        addBuyBackProductsReq: addBuyBackProductsReqCallback,
    };
};
